/**
 * 身份持久化与哈希路由（零依赖，离线可用）。
 */
import { useEffect, useState } from 'react';
import type { IdentityInput } from '../state/types.ts';

const IDENTITY_KEY = 'zongheng.identity.v1';

export interface StoredIdentity extends IdentityInput {
  id: string;
}

export function makeParticipantId(): string {
  const random = globalThis.crypto?.getRandomValues?.(new Uint8Array(8));
  if (random) {
    return [...random].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function loadIdentity(): StoredIdentity | null {
  try {
    const raw = globalThis.localStorage?.getItem(IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredIdentity;
    if (!parsed?.name || !parsed?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveIdentity(identity: StoredIdentity): void {
  try {
    globalThis.localStorage?.setItem(IDENTITY_KEY, JSON.stringify(identity));
  } catch {
    /* 隐私模式下忽略 */
  }
}

export function normalizeIdentity(input: Partial<IdentityInput> | null | undefined): StoredIdentity {
  const role = input?.role === 'TEACHER' ? 'TEACHER' : 'DEBATER';
  return {
    id: makeParticipantId(),
    name: (input?.name ?? '').trim() || (role === 'TEACHER' ? '教练' : '辩手'),
    role,
    side: role === 'DEBATER' ? (input?.side ?? 'AFF') : undefined,
    seat: role === 'DEBATER' ? (input?.seat ?? 1) : undefined,
  };
}

/* -------------------------------- 路由 -------------------------------- */

export function readRoute(): string {
  const hash = globalThis.location?.hash?.replace(/^#/, '') ?? '/';
  return hash === '' ? '/' : hash;
}

export function navigate(path: string): void {
  if (globalThis.location) {
    globalThis.location.hash = path;
  }
}

export function useHashPath(): string {
  const [path, setPath] = useHashPathState();
  return path || '/';
}

function useHashPathState(): [string, (path: string) => void] {
  const [path, setPath] = useState(() => readRoute());
  useEffect(() => {
    const onChange = () => setPath(readRoute());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const navigateTo = (next: string) => {
    navigate(next);
    setPath(next);
  };
  return [path, navigateTo];
}

export function useNavigate(): (path: string) => void {
  const [, setPath] = useHashPathState();
  return (path: string) => {
    navigate(path);
    setPath(path);
  };
}

export function roomLink(roomCode: string): string {
  const origin = globalThis.location?.origin ?? 'http://127.0.0.1:5173';
  return `${origin}/#/join/${roomCode}`;
}

export function parseJoinCode(path: string): string | null {
  const match = /^\/join\/([A-Za-z0-9]{4,8})/.exec(path);
  return match?.[1]?.toUpperCase() ?? null;
}
